import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Sidebar } from "../components/Sidebar";
import { UserAvatar } from "../components/team/UserAvatar";
import { InviteModal } from "../components/team/InviteModal";
import {
  UserPlus, MoreHorizontal, Copy, RefreshCcw, X, Crown,
  Activity, Clock, ShieldCheck, AlertCircle, Loader2, ChevronDown, Check,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useAuth } from "../data/authStore";
import { ROLES, ROLE_LABEL, canManageTeam } from "../lib/roles";
import { PremiumInput } from "../components/brand/PremiumInput";

const STATUS_FILTERS = [
  { id: "all",      label: "Everyone" },
  { id: "active",   label: "Active" },
  { id: "pending",  label: "Pending" },
  { id: "disabled", label: "Disabled" },
];

const formatRelative = (iso) => {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "—";
  const diff = Math.max(0, Date.now() - t);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  const wks = Math.floor(days / 7);
  if (wks < 5) return `${wks}w ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

// ─── Role pill ───
const RolePill = ({ role, className = "" }) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100 ${className}`}
    data-testid={`role-pill-${role}`}
  >
    {ROLE_LABEL[role] || "Member"}
  </span>
);

// ─── Inline role dropdown for member rows ───
const RoleDropdown = ({ value, disabled, onChange }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[12px] font-semibold transition-all ${
          disabled
            ? "bg-slate-100 text-slate-500 cursor-not-allowed"
            : "bg-white border border-slate-200 hover:border-slate-300 text-slate-800"
        }`}
        data-testid="role-dropdown-trigger"
      >
        {ROLE_LABEL[value] || "Member"}
        {!disabled && <ChevronDown size={12} className="text-slate-400" />}
      </button>
      {open && !disabled && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 z-20 w-[220px] rounded-xl bg-white border border-slate-200 shadow-[0_18px_40px_-10px_rgba(15,23,42,0.22)] overflow-hidden">
            {ROLES.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => { onChange?.(r.id); setOpen(false); }}
                className={`w-full text-left px-3 py-2 hover:bg-indigo-50/60 flex items-center justify-between ${value === r.id ? "bg-indigo-50/80" : ""}`}
                data-testid={`role-dropdown-${r.id}`}
              >
                <span className="text-[12.5px] font-semibold text-slate-900">{r.label}</span>
                {value === r.id && <Check size={12} className="text-indigo-600" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ─── Member row ───
const MemberRow = ({ member, currentUserId, canManage, onRoleChange, onRemove }) => {
  const [open, setOpen] = useState(false);
  const isMe = member.user_id === currentUserId;
  const lockRoleEdit = !canManage || member.is_owner || isMe;

  return (
    <div
      className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50/60 transition-colors"
      data-testid={`member-row-${member.user_id}`}
    >
      <UserAvatar name={member.full_name} email={member.email} size={38} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-[13.5px] font-semibold text-slate-900 truncate">
            {member.full_name || member.email}
            {isMe && <span className="ml-1.5 text-[10.5px] text-indigo-600 font-semibold">(you)</span>}
          </p>
          {member.is_owner && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[9.5px] font-semibold uppercase tracking-[0.16em]">
              <Crown size={9} /> Owner
            </span>
          )}
          {member.status === "disabled" && (
            <span className="px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-700 text-[9.5px] font-semibold uppercase tracking-[0.16em]">
              Disabled
            </span>
          )}
        </div>
        <p className="text-[11.5px] text-slate-500 truncate">{member.email}</p>
      </div>
      <div className="hidden md:flex flex-col items-end text-right min-w-[110px]">
        <span className="text-[10.5px] uppercase tracking-[0.18em] text-slate-400 font-semibold">Last active</span>
        <span className="text-[12px] text-slate-700 font-medium">{formatRelative(member.last_active_at)}</span>
      </div>
      <div className="hidden sm:block">
        <RoleDropdown value={member.role} disabled={lockRoleEdit} onChange={(role) => onRoleChange?.(member, role)} />
      </div>
      <div className="sm:hidden">
        <RolePill role={member.role} />
      </div>
      {canManage && !member.is_owner && !isMe && (
        <div className="relative">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="w-8 h-8 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition-colors"
            data-testid={`member-menu-${member.user_id}`}
          >
            <MoreHorizontal size={15} />
          </button>
          {open && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
              <div className="absolute right-0 mt-2 z-20 w-[180px] rounded-xl bg-white border border-slate-200 shadow-[0_18px_40px_-10px_rgba(15,23,42,0.22)] overflow-hidden">
                {member.status === "active" ? (
                  <button
                    type="button"
                    onClick={() => { setOpen(false); onRoleChange?.(member, member.role, { status: "disabled" }); }}
                    className="w-full text-left px-3 py-2 text-[12.5px] text-slate-800 hover:bg-slate-50"
                    data-testid={`member-disable-${member.user_id}`}
                  >
                    Disable access
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => { setOpen(false); onRoleChange?.(member, member.role, { status: "active" }); }}
                    className="w-full text-left px-3 py-2 text-[12.5px] text-slate-800 hover:bg-slate-50"
                    data-testid={`member-enable-${member.user_id}`}
                  >
                    Re-enable access
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { setOpen(false); onRemove?.(member); }}
                  className="w-full text-left px-3 py-2 text-[12.5px] text-rose-600 hover:bg-rose-50"
                  data-testid={`member-remove-${member.user_id}`}
                >
                  Remove from workspace
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Pending invite row ───
const InviteRow = ({ invite, canManage, onCopy, onResend, onRevoke }) => {
  const expired = invite.status === "expired";
  return (
    <div
      className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50/60 transition-colors"
      data-testid={`invite-row-${invite.id}`}
    >
      <span className="w-9 h-9 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 text-slate-600 flex items-center justify-center">
        <Clock size={15} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-[13.5px] font-semibold text-slate-900 truncate">{invite.email}</p>
          <span
            className={`px-1.5 py-0.5 rounded-full text-[9.5px] font-semibold uppercase tracking-[0.16em] border ${
              expired
                ? "bg-rose-50 text-rose-700 border-rose-200"
                : invite.status === "revoked"
                ? "bg-slate-100 text-slate-600 border-slate-200"
                : invite.status === "accepted"
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-amber-50 text-amber-700 border-amber-200"
            }`}
          >
            {invite.status}
          </span>
        </div>
        <p className="text-[11.5px] text-slate-500 truncate">
          Invited by {invite.invited_by_name || "—"} · {formatRelative(invite.created_at)} · expires {formatRelative(invite.expires_at)}
        </p>
      </div>
      <div className="hidden sm:block">
        <RolePill role={invite.role} />
      </div>
      {canManage && invite.status === "pending" && (
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onCopy?.(invite)}
            title="Copy invite link"
            className="w-8 h-8 rounded-lg text-slate-500 hover:text-indigo-700 hover:bg-indigo-50 flex items-center justify-center transition-colors"
            data-testid={`invite-copy-${invite.id}`}
          >
            <Copy size={14} />
          </button>
          <button
            type="button"
            onClick={() => onResend?.(invite)}
            title="Generate new link"
            className="w-8 h-8 rounded-lg text-slate-500 hover:text-indigo-700 hover:bg-indigo-50 flex items-center justify-center transition-colors"
            data-testid={`invite-resend-${invite.id}`}
          >
            <RefreshCcw size={14} />
          </button>
          <button
            type="button"
            onClick={() => onRevoke?.(invite)}
            title="Revoke invite"
            className="w-8 h-8 rounded-lg text-slate-500 hover:text-rose-600 hover:bg-rose-50 flex items-center justify-center transition-colors"
            data-testid={`invite-revoke-${invite.id}`}
          >
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Activity item ───
const ActivityIcon = ({ type }) => {
  const cls = "w-7 h-7 rounded-lg flex items-center justify-center shrink-0";
  if (type === "invite_sent" || type === "invite_resent")
    return <span className={`${cls} bg-indigo-50 text-indigo-700 border border-indigo-100`}><UserPlus size={13} /></span>;
  if (type === "invite_accepted" || type === "member_joined")
    return <span className={`${cls} bg-emerald-50 text-emerald-700 border border-emerald-100`}><ShieldCheck size={13} /></span>;
  if (type === "invite_revoked" || type === "member_removed" || type === "member_disabled")
    return <span className={`${cls} bg-rose-50 text-rose-700 border border-rose-100`}><AlertCircle size={13} /></span>;
  return <span className={`${cls} bg-slate-100 text-slate-600 border border-slate-200`}><Activity size={13} /></span>;
};

// ─── Page ───
export default function TeamPage() {
  const { user } = useAuth();
  const canManage = canManageTeam(user?.role);

  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showInvite, setShowInvite] = useState(false);

  const load = useCallback(async () => {
    try {
      const [m, i, a] = await Promise.all([
        api.get("/api/team/members"),
        api.get("/api/team/invites"),
        api.get("/api/team/activity?limit=30"),
      ]);
      setMembers(m.data || []);
      setInvites(i.data || []);
      setActivity(a.data || []);
    } catch (err) {
      toast.error("Couldn't load team", { description: apiError(err) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pendingInvites = useMemo(
    () => invites.filter((i) => i.status === "pending"),
    [invites]
  );

  const filteredMembers = useMemo(() => {
    let list = members;
    if (statusFilter === "active") list = list.filter((m) => m.status === "active");
    if (statusFilter === "disabled") list = list.filter((m) => m.status === "disabled");
    if (statusFilter === "pending") return [];
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((m) =>
        (m.full_name || "").toLowerCase().includes(q) ||
        (m.email || "").toLowerCase().includes(q) ||
        (m.role_label || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [members, search, statusFilter]);

  const filteredInvites = useMemo(() => {
    if (statusFilter === "active" || statusFilter === "disabled") return [];
    // Accepted / revoked invitations are not pending — accepted people appear under Members.
    let list = invites.filter((i) => i.status !== "accepted" && i.status !== "revoked");
    if (statusFilter === "pending") list = list.filter((i) => i.status === "pending");
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((i) =>
        (i.email || "").toLowerCase().includes(q) ||
        (i.role_label || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [invites, search, statusFilter]);

  const handleRoleChange = async (member, newRole, extra = {}) => {
    if (member.role === newRole && !extra.status) return;
    try {
      const body = {};
      if (newRole && member.role !== newRole) body.role = newRole;
      if (extra.status) body.status = extra.status;
      const { data } = await api.patch(`/api/team/members/${member.id}`, body);
      setMembers((prev) => prev.map((m) => (m.id === data.id ? data : m)));
      toast.success(
        extra.status
          ? `${member.full_name || member.email} ${extra.status === "disabled" ? "disabled" : "re-enabled"}`
          : `Updated ${member.full_name || member.email} to ${data.role_label}`
      );
      load();
    } catch (err) {
      toast.error("Couldn't update member", { description: apiError(err) });
    }
  };

  const handleRemove = async (member) => {
    if (!window.confirm(`Remove ${member.full_name || member.email} from this workspace?`)) return;
    try {
      await api.delete(`/api/team/members/${member.id}`);
      setMembers((prev) => prev.filter((m) => m.id !== member.id));
      toast.success(`${member.full_name || member.email} removed`);
      load();
    } catch (err) {
      toast.error("Couldn't remove member", { description: apiError(err) });
    }
  };

  const handleCopyInvite = async (invite) => {
    if (!invite.invite_url) {
      toast.error("Link not available — try resending the invite.");
      return;
    }
    try {
      const url = invite.invite_url.startsWith("http")
        ? invite.invite_url
        : `${window.location.origin}${invite.invite_url}`;
      await navigator.clipboard.writeText(url);
      toast.success("Invite link copied to clipboard");
    } catch {
      toast.error("Couldn't copy — please copy manually");
    }
  };

  const handleResend = async (invite) => {
    try {
      const { data } = await api.post(`/api/team/invites/${invite.id}/resend`);
      setInvites((prev) => prev.map((i) => (i.id === data.id ? data : i)));
      const url = data.invite_url?.startsWith("http") ? data.invite_url : `${window.location.origin}${data.invite_url}`;
      try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
      toast.success("Generated a new invite link", { description: "Copied to clipboard." });
    } catch (err) {
      toast.error("Couldn't resend invite", { description: apiError(err) });
    }
  };

  const handleRevoke = async (invite) => {
    if (!window.confirm(`Revoke invite for ${invite.email}?`)) return;
    try {
      await api.delete(`/api/team/invites/${invite.id}`);
      setInvites((prev) => prev.map((i) => (i.id === invite.id ? { ...i, status: "revoked" } : i)));
      toast.success(`Invite for ${invite.email} revoked`);
      load();
    } catch (err) {
      toast.error("Couldn't revoke invite", { description: apiError(err) });
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--cf-app-bg)" }}>
      <Sidebar />
      <main className="md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out px-4 md:px-10 py-10" data-testid="team-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 max-w-[1280px] mx-auto">
          <div>
            <p className="text-[10.5px] uppercase tracking-[0.22em] text-indigo-600 font-semibold">Workspace</p>
            <h1 className="font-display text-[34px] md:text-[42px] font-bold tracking-[-0.025em] text-slate-900 mt-1.5 leading-[1.05]">Team</h1>
            <p className="text-[14px] text-slate-500 mt-1.5 max-w-[560px] leading-relaxed">
              Invite directors, managers, negotiators and progressors. Assign granular roles and keep an audit trail of every change.
            </p>
          </div>
          {canManage && (
            <button
              type="button"
              onClick={() => setShowInvite(true)}
              className="inline-flex items-center gap-2 h-11 px-5 rounded-xl text-white text-[13.5px] font-semibold transition-all"
              style={{
                background: "linear-gradient(180deg, #6366F1 0%, #4F6BFF 100%)",
                boxShadow: "0 14px 30px -12px rgba(99, 102, 241,0.55), inset 0 1px 0 rgba(255,255,255,0.18)",
              }}
              data-testid="team-invite-btn"
            >
              <UserPlus size={14} /> Invite member
            </button>
          )}
        </div>

        {/* Toolbar */}
        <div className="max-w-[1280px] mx-auto mt-7 flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex-1 max-w-[420px]">
            <PremiumInput
              value={search}
              onChange={setSearch}
              placeholder="Search by name, email, role…"
              format="search"
              size="lg"
              clearable
              testid="team-search"
            />
          </div>
          <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-slate-100/80 border border-slate-200">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setStatusFilter(f.id)}
                className={`px-3 h-9 rounded-lg text-[12.5px] font-semibold transition-all ${
                  statusFilter === f.id
                    ? "bg-white text-slate-900 shadow-[0_4px_12px_-4px_rgba(15,23,42,0.18)]"
                    : "text-slate-500 hover:text-slate-800"
                }`}
                data-testid={`team-filter-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2 text-[12px] text-slate-500">
            <span className="cf-num font-semibold text-slate-700">{members.length}</span> members ·
            <span className="cf-num font-semibold text-amber-700">{pendingInvites.length}</span> pending
          </div>
        </div>

        {/* Body grid */}
        <div className="max-w-[1280px] mx-auto mt-6 grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-6">
          {/* Left: members + invites */}
          <div className="space-y-6">
            {/* Pending invites */}
            {filteredInvites.length > 0 && (
              <section className="rounded-2xl bg-white border border-slate-200/80 shadow-[0_4px_18px_-6px_rgba(15,23,42,0.06)] overflow-hidden" data-testid="pending-invites-section">
                <header className="px-4 py-3 flex items-center justify-between border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <Clock size={14} className="text-amber-500" />
                    <h2 className="text-[13px] font-semibold text-slate-900">Pending invites</h2>
                  </div>
                  <span className="text-[11px] text-slate-500">{filteredInvites.length}</span>
                </header>
                <div className="divide-y divide-slate-100">
                  {filteredInvites.map((inv) => (
                    <InviteRow
                      key={inv.id}
                      invite={inv}
                      canManage={canManage}
                      onCopy={handleCopyInvite}
                      onResend={handleResend}
                      onRevoke={handleRevoke}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Members */}
            <section className="rounded-2xl bg-white border border-slate-200/80 shadow-[0_4px_18px_-6px_rgba(15,23,42,0.06)] overflow-hidden" data-testid="members-section">
              <header className="px-4 py-3 flex items-center justify-between border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={14} className="text-indigo-500" />
                  <h2 className="text-[13px] font-semibold text-slate-900">Members</h2>
                </div>
                <span className="text-[11px] text-slate-500">{filteredMembers.length}</span>
              </header>
              <div className="divide-y divide-slate-100">
                {loading && (
                  <div className="px-4 py-10 flex items-center justify-center text-slate-500 text-[13px]">
                    <Loader2 size={14} className="animate-spin mr-2" /> Loading team…
                  </div>
                )}
                {!loading && filteredMembers.length === 0 && statusFilter !== "pending" && (
                  <div className="px-4 py-10 text-center">
                    <p className="text-[13px] text-slate-500">No members match your filters.</p>
                  </div>
                )}
                {filteredMembers.map((m) => (
                  <MemberRow
                    key={m.id}
                    member={m}
                    currentUserId={user?.id}
                    canManage={canManage}
                    onRoleChange={handleRoleChange}
                    onRemove={handleRemove}
                  />
                ))}
              </div>
            </section>
          </div>

          {/* Right: activity */}
          <aside className="rounded-2xl bg-white border border-slate-200/80 shadow-[0_4px_18px_-6px_rgba(15,23,42,0.06)] overflow-hidden h-fit" data-testid="team-activity-section">
            <header className="px-4 py-3 flex items-center justify-between border-b border-slate-100">
              <div className="flex items-center gap-2">
                <Activity size={14} className="text-indigo-500" />
                <h2 className="text-[13px] font-semibold text-slate-900">Activity</h2>
              </div>
              <span className="text-[11px] text-slate-500">Last {activity.length}</span>
            </header>
            <div className="max-h-[640px] overflow-y-auto cf-scroll">
              {activity.length === 0 && !loading && (
                <div className="px-4 py-8 text-center text-[12.5px] text-slate-500">
                  No activity yet — invite a teammate to get started.
                </div>
              )}
              {activity.map((a) => (
                <div key={a.id} className="px-4 py-3 flex items-start gap-3 border-b border-slate-50 last:border-b-0" data-testid={`activity-${a.id}`}>
                  <ActivityIcon type={a.type} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[12.5px] text-slate-800 leading-snug">{a.summary}</p>
                    <p className="text-[10.5px] text-slate-400 mt-0.5">
                      {a.actor_name || "System"} · {formatRelative(a.created_at)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </aside>
        </div>

        <InviteModal
          open={showInvite}
          onClose={() => setShowInvite(false)}
          onCreated={() => load()}
        />
      </main>
    </div>
  );
}
